import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, Timestamp } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "../../components/feature/NavBar";
import { db } from "../../utils/firebase";
import { useAuth } from "../../utils/AuthContext";
import { notify } from "utils/notify";
import Footer from "../../components/feature/Footer";
import DiscoverFilters, { CategoryOption, TypeOption } from "components/feature/DiscoverFilters";
import { usePageMeta } from "../../hooks/usePageMeta";
import { normalizeEntity } from "../../lib/catalog";

export interface Entity {
  id: string;
  name: string;
  description?: string;
  websiteURL: string;
  logoURL: string[]; // Array of logo URLs
  createdAt: Timestamp;
}

const Brands: React.FC = () => {
  usePageMeta("Brands");
  const [brands, setBrands] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hoveredBrandId, setHoveredBrandId] = useState<string | null>(null);
  const [displayDeleteConfirmation, setDisplayDeleteConfirmation] = useState<string | null>(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<TypeOption>("Brands");
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption>("All");

  useEffect(() => {
    async function fetchBrands() {
      try {
        const querySnapshot = await getDocs(collection(db, "companies"));
        const brandData: Entity[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(normalizeEntity(doc.id, doc.data()) as Omit<Entity, "id">),
        }));
        setBrands(brandData);
      } catch (error) {
        console.error("Error fetching brands:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchBrands();
  }, []);

  const deleteBrand = async (brandId: string) => {
    try {
      await deleteDoc(doc(db, "companies", brandId));
      setBrands((prevBrands) => prevBrands.filter((brand) => brand.id !== brandId));
    } catch (error) {
      notify({ isSuccess: false, message: "Error deleting brand:", info: String(error) });
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 md:px-20 px-8">
      <NavBar />
      {displayDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white px-4 py-6 rounded-md">
            <h1 className="text-3xl font-semibold">Are you sure you want to delete this brand?</h1>
            <p className="text-gray-600 mt-2">This action cannot be undone.</p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="bg-red-600 text-white px-4 py-2 rounded-full w-full"
                onClick={() => {
                  deleteBrand(displayDeleteConfirmation);
                  setDisplayDeleteConfirmation(null);
                }}
              >
                Delete
              </button>
              <button
                className="bg-black text-white px-4 py-2 rounded-full w-full"
                onClick={() => setDisplayDeleteConfirmation(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <DiscoverFilters
          title="Brands"
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          showCategories={false}
        /> 
      <div className="max-w-[100vw]  mx-auto py-8 px-4">  
        {loading ? (
          <p className="text-gray-500">Loading brands...</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="relative bg-white shadow rounded p-4 flex items-center cursor-pointer gap-3"
                onMouseEnter={() => setHoveredBrandId(brand.id)}
                onMouseLeave={() => setHoveredBrandId(null)}
                onClick={() => navigate(`/brand/${brand.id}`)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    navigate(`/brand/${brand.id}`);
                  }
                }}
              >
                {brand.logoURL && brand.logoURL.length > 0 && (
                  <img
                    src={brand.logoURL[0]}
                    alt={`${brand.name} Logo`}
                    className="w-10 h-10 object-cover rounded"
                  />
                )}
                <div className="flex flex-col items-start gap-2 w-[80%]">
                  <h2 className="font-semibold text-base">{brand.name}</h2>
                  {brand.description && (
                    <p className="text-gray-600 text-sm text-start w-full truncate">
                      {brand.description}
                    </p>
                  )}
                </div>
                {/* Admin Controls */}
                {isAdmin && hoveredBrandId === brand.id && (
                  <div className="absolute inset-0 flex p-4 justify-end gap-2">
                    <button
                      className="bg-red-600 text-white px-4 py-2 rounded-full h-max flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDisplayDeleteConfirmation(brand.id);
                      }}
                    >
                      Delete
                    </button>
                    <Link
                      to={`/admin/brand/${brand.id}`}
                      className="bg-black text-white px-4 py-2 rounded-full h-max flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Modify
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Brands;
